<template>
  <div class="project-management">
    <h2>Gestione Progetti</h2>
    
    <!-- Alerts -->
    <div v-if="alert.show" :class="`alert alert-${alert.type}`" class="alert-dismissible">
      {{ alert.message }}
      <button @click="alert.show = false" class="btn-close"></button>
    </div>
    
    <!-- Header con pulsante Nuovo Progetto -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4>Lista Progetti ({{ projects.length }})</h4>
      <div class="d-flex gap-2">
        <button @click="loadProjects" class="btn btn-outline-primary" title="Ricarica lista">
          <i class="fas fa-refresh"></i> Ricarica
        </button>
        <button 
          class="btn btn-primary"
          @click="openNewProjectDialog"
          title="Aggiungi nuovo progetto"
        >
          Nuovo Progetto
        </button>
      </div>
    </div>
    
    <!-- Projects Table -->
    <div v-if="isLoading && projects.length === 0" class="card">
      <div class="card-body text-center py-4">
        <div class="spinner-border"></div>
        <p class="mt-2">Caricamento progetti...</p>
      </div>
    </div>
    
    <div v-else-if="projects.length === 0" class="card">
      <div class="card-body text-center py-4 text-muted">
        <p>Nessun progetto trovato.</p>
        <p>Clicca su "Nuovo Progetto" per creare il primo.</p>
      </div>
    </div>
    
    <TableSortable v-else :data="projects" :options="projectsTableOptions">
      <template #field-codice_progetto="{ row }">
        {{ row.codice_progetto }}
      </template>
      
      <template #field-project_name="{ row }">
        <strong>{{ row.project_name }}</strong>
        <div v-if="row.acronimo" class="text-muted small">
          {{ row.acronimo }}
        </div>
      </template>
      
      <template #field-stato_label="{ row }">
        <span :class="getStateBadgeClass(row.stato)">
          {{ row.stato_label || 'In Corso' }}
        </span>
      </template>
      
      <template #field-data_inizio="{ row }">
        <span v-if="row.data_inizio">{{ formatDateShort(row.data_inizio) }}</span>
        <span v-else class="text-muted">-</span>
      </template>
      
      <template #field-data_fine="{ row }">
        <span v-if="row.data_fine">{{ formatDateShort(row.data_fine) }}</span>
        <span v-else class="text-muted">-</span>
      </template>
      
      <template #field-budget="{ row }">
        € {{ formatCurrency(row.budget) }}
        <div class="small mt-1">
          <span
            :class="{
              'badge bg-success': row.budget_years_sum == row.budget,
              'badge bg-warning': row.budget_years_sum < row.budget,
              'badge bg-danger': row.budget_years_sum > row.budget
            }"
          >
            Allocato: € {{ formatCurrency(row.budget_years_sum) }}
          </span>
        </div>
      </template>
      
      <template #field-budget_rendicontato="{ row }">
        € {{ formatCurrency(row.budget_rendicontato) }}
        <div class="small mt-1">
          <span :class="calcClass(row)">
            {{
              row.budget > 0
                ? ((row.budget_rendicontato) / row.budget * 100).toFixed(2) + '%'
                : 'N/A'
            }}
          </span>
        </div>
      </template>
      
      <template #field-actions="{ row }">
        <div class="btn-group">
          <button 
            @click="goToProjectDetail(row.id_project)"
            class="btn btn-sm btn-outline-success"
            :disabled="row.deleted"
            title="Dettaglio e Budget Anni"
          >
            <i class="fas fa-chart-line"></i> Dettaglio
          </button>
          <button 
            @click="openEditProjectDialog(row)"
            class="btn btn-sm btn-outline-primary"
            :disabled="row.deleted"
            title="Modifica"
          >
            <i class="fas fa-edit"></i>
          </button>
          <button 
            @click="confirmDelete(row)"
            class="btn btn-sm btn-outline-danger"
            :disabled="row.deleted"
            title="Elimina"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </template>
    </TableSortable>

    <!-- Dialog Modal per Progetto -->
    <div 
      v-if="showDialog" 
      class="modal fade show" 
      style="display: block; background-color: rgba(0,0,0,0.5);"
      tabindex="-1" 
      role="dialog"
      @click="handleModalClick"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingProject ? 'Modifica Progetto' : 'Nuovo Progetto' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeDialog"
              aria-label="Chiudi"
            ></button>
          </div>
          
          <div class="modal-body">
            <!-- Error nel dialog -->
            <div v-if="dialogError" class="alert alert-danger">
              {{ dialogError }}
            </div>
            
            <!-- DbmngForm Component -->
            <DbmngForm
              v-if="projectSchema"
              v-model="projectForm"
              :dbmng-schema="projectSchema"
              :show-actions="false"
              :validate-on-change="true"
              @validate="onFormValidate"
            />
            
            <!-- Loading durante recupero schema -->
            <div v-else class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Caricamento schema...</span>
              </div>
              <p class="mt-2">Caricamento form...</p>
            </div>
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeDialog"
              :disabled="isDialogLoading"
            >
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveProject"
              :disabled="isDialogLoading || !isFormValid"
            >
              <span v-if="isDialogLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingProject ? 'Aggiorna' : 'Crea' }} Progetto
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="deleteConfirm.show" class="modal d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Conferma Eliminazione</h5>
            <button @click="deleteConfirm.show = false" class="btn-close"></button>
          </div>
          <div class="modal-body">
            <p>Sei sicuro di voler eliminare il progetto:</p>
            <p><strong>{{ deleteConfirm.project?.project_name }}</strong></p>
            <p class="text-muted small">L'operazione può essere annullata.</p>
          </div>
          <div class="modal-footer">
            <button @click="deleteConfirm.show = false" class="btn btn-secondary">
              Annulla
            </button>
            <button @click="deleteProject" class="btn btn-danger">
              Elimina Progetto
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="deleteConfirm.show" class="modal-backdrop"></div>
  </div>
</template>

<script>
import ProjectAPI from '../services/projectAPI.js';
import DbmngForm from '@/lib/DbmngForm.vue';
import TableSortable from '@/components/TableSortable.vue';

export default {
  name: 'ProjectManagement',
  components: {
    DbmngForm,
    TableSortable
  },
  
  data() {
    return {
      projects: [],
      projectForm: {},
      projectSchema: null, // Schema DBMng per il form
      peopleOptions: [], // Opzioni per select responsabile (se non gestito da foreign_key)
      editingProject: null,
      formErrors: {},
      isFormValid: true,
      isLoading: false,
      alert: {
        show: false,
        type: 'success',
        message: ''
      },
      deleteConfirm: {
        show: false,
        project: null
      },
      // Dialog state
      showDialog: false,
      isDialogLoading: false,
      dialogError: null
    };
  },
  
  async mounted() {
    await this.loadProjects();
  },
  
  methods: {


    calcClass(project) {

      if (project.budget > 0 && ((project.budget_rendicontato / project.budget) > 1)) {
        return 'text-extreme-danger fw-bold';
      }
      else if (project.budget > 0 && ((project.budget_rendicontato / project.budget) >= 0.9)) {
        return 'text-success fw-bold';
      } else if (project.budget > 0 && ((project.budget_rendicontato / project.budget) < 0.9)) {
        return 'text-warning fw-bold';
      } else if (project.budget_rendicontato === 0) {
        return 'text-danger fw-bold';
      }
    },

    /**
     * Naviga al dettaglio del progetto per gestire budget anni
     */
    goToProjectDetail(projectId) {
      this.$router.push(`/projects/${projectId}`)
    },
    
    /**
     * Carica la lista dei progetti
     */
    async loadProjects() {
      this.isLoading = true;
      try {
        const projectAPI = new ProjectAPI();
        const result = await projectAPI.getProjects();
        
        if (result.ok) {
          this.projects = result.data;
          
          // Salva schema DBMng per il form
          if (result.dbmng) {
            this.projectSchema = result.dbmng;
            console.log('Schema DBMng caricato:', this.projectSchema);
          }
          
          // Carica anche opzioni persone se disponibili (per compatibilità)
          this.peopleOptions = result.people_options || [];
          
          this.showAlert('success', `Caricati ${result.data.length} progetti`);
        } else {
          this.showAlert('danger', result.message || 'Errore nel caricamento progetti');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },
    
    /**
     * Gestisce validazione form dalla componente DbmngForm
     */
    onFormValidate(validation) {
      this.isFormValid = validation.valid;
      this.formErrors = validation.errors || [];
    },
    
    /**
     * Salva progetto (crea o aggiorna) - ora usa DbmngForm
     */
    async saveProject() {
      // Controllo validità form prima di procedere
      if (!this.isFormValid) {
        this.dialogError = 'Correggi i campi in errore prima di salvare';
        return;
      }
      
      this.isDialogLoading = true;
      this.dialogError = null;
      
      try {
        const projectAPI = new ProjectAPI();
        let result;
        
        if (this.editingProject) {
          // Aggiorna progetto esistente
          result = await projectAPI.updateProject(this.editingProject.id_project, this.projectForm);
        } else {
          // Crea nuovo progetto
          result = await projectAPI.createProject(this.projectForm);
        }
        
        if (result.ok) {
          this.showAlert('success', 
            this.editingProject ? 'Progetto aggiornato con successo' : 'Progetto creato con successo'
          );
          
          this.closeDialog();
          await this.loadProjects();
        } else {
          this.dialogError = result.message || 'Errore nel salvataggio';
        }
      } catch (error) {
        this.dialogError = error.message || 'Errore di connessione';
      } finally {
        this.isDialogLoading = false;
      }
    },

    /**
     * Apre dialog per nuovo progetto
     */
    openNewProjectDialog() {
      this.editingProject = null;
      this.resetForm();
      this.dialogError = null;
      this.isFormValid = true;
      this.showDialog = true;
    },

    /**
     * Apre dialog per modifica progetto
     */
    openEditProjectDialog(project) {
      this.editingProject = project;
      
      // Inizializza form con i dati del progetto
      this.projectForm = {
        project_name: project.project_name || '',
        acronimo: project.acronimo || '',
        codice_progetto: project.codice_progetto || '',
        data_inizio: project.data_inizio || '',
        data_fine: project.data_fine || '',
        stato: project.stato !== undefined ? project.stato : 0,
        responsabile: project.responsabile || null,
        cup: project.cup || '',
        budget: project.budget ? parseFloat(project.budget) : null,
        id_organisation: project.id_organisation // Importante per l'aggiornamento
      };
      
      this.formErrors = [];
      this.dialogError = null;
      this.isFormValid = true;
      this.showDialog = true;
    },

    /**
     * Chiude dialog e resetta stato
     */
    closeDialog() {
      this.showDialog = false;
      this.editingProject = null;
      this.dialogError = null;
      this.isDialogLoading = false;
      this.isFormValid = true;
      this.resetForm();
    },

    /**
     * Gestisce click fuori dal dialog
     */
    handleModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDialog();
      // }
    },

    /**
     * Modifica progetto esistente - metodo deprecated
     */
    editProject(project) {
      // Ora usa openEditProjectDialog
      this.openEditProjectDialog(project);
    },
    
    /**
     * Conferma eliminazione progetto
     */
    confirmDelete(project) {
      this.deleteConfirm = {
        show: true,
        project: project
      };
    },
    
    /**
     * Elimina progetto
     */
    async deleteProject() {
      try {
        const projectAPI = new ProjectAPI();
        const result = await projectAPI.deleteProject(this.deleteConfirm.project.id_project);
        
        if (result.ok) {
          this.showAlert('success', 'Progetto eliminato con successo');
          await this.loadProjects();
        } else {
          this.showAlert('danger', result.message || 'Errore nell\'eliminazione');
        }
      } catch (error) {
        this.showAlert('danger', `Errore: ${error.message}`);
      } finally {
        this.deleteConfirm.show = false;
      }
    },
    
    /**
     * Reset form
     */
    resetForm() {
      this.projectForm = {};
      this.editingProject = null;
      this.formErrors = [];
      this.isFormValid = true;
    },
    
    /**
     * Mostra alert
     */
    showAlert(type, message) {
      this.alert = {
        show: true,
        type: type,
        message: message
      };
      
      // Auto-hide success alerts
      if (type === 'success') {
        setTimeout(() => {
          this.alert.show = false;
        }, 5000);
      }
    },
    
    /**
     * Formatta valuta
     */
    formatCurrency(value) {
      return parseFloat(value).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    /**
     * Formatta data
     */
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /**
     * Formatta data breve (solo giorno/mese/anno)
     */
    formatDateShort(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('it-IT', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      });
    },

    /**
     * Restituisce classi CSS per badge stato
     */
    getStateBadgeClass(stato) {
      const baseClass = 'badge ';
      switch (parseInt(stato)) {
        case 0:
          return baseClass + 'bg-success';  // In corso = verde
        case 1:
          return baseClass + 'bg-secondary'; // Chiuso = grigio
        default:
          return baseClass + 'bg-warning';   // Sconosciuto = giallo
      }
    }
  },
  
  computed: {
    projectsTableOptions() {
      return {
        structure: [
          { key: 'codice_progetto', label: 'ID', sortable: true },
          { key: 'project_name', label: 'Nome Progetto', sortable: true, filterable: true },
          { key: 'stato_label', label: 'Stato', sortable: true },
          { key: 'data_inizio', label: 'Data Inizio', sortable: true },
          { key: 'data_fine', label: 'Data Fine', sortable: true },
          { key: 'budget', label: 'Totale', sortable: true, align: 'right' },
          { key: 'budget_rendicontato', label: 'Rendicontato', sortable: true, align: 'right' },
          { key: 'actions', label: 'Azioni', sortable: false, align: 'right' }
        ]
      }
    }
  }
};
</script>

<style scoped>

.project-management {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.modal {
  background: rgba(0, 0, 0, 0.5);
}

.alert {
  border: none;
  border-radius: 8px;
}

.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0 !important;
}

.table th {
  border-top: none;
  font-weight: 600;
  background: #f8f9fa;
}

.btn {
  border-radius: 6px;
}

.form-control {
  border-radius: 6px;
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  border-radius: 12px;
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  border-radius: 12px 12px 0 0;
}

.modal-title {
  font-weight: 600;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.8;
}

.btn-close:before {
  content: '×';
}

.badge {
  font-size: 0.75em;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.modal-backdrop {
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1040;
}

@media (max-width: 768px) {
  .project-management {
    padding: 10px;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .btn-group .btn {
    padding: 0.25rem 0.5rem;
  }
  
  /* Nascondi alcune colonne su mobile per risparmiare spazio */
  .table th:nth-child(3),
  .table th:nth-child(6),
  .table th:nth-child(7),
  .table td:nth-child(3),
  .table td:nth-child(6),
  .table td:nth-child(7) {
    display: none;
  }
}

/* Stili per i nuovi campi */
.form-group {
  margin-bottom: 1rem;
}

.badge {
  font-size: 0.75em;
  font-weight: 500;
}

.text-muted {
  color: #6c757d !important;
}

/* Miglioramenti per date e select */
.form-select,
.form-control[type="date"] {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
}

.form-select:focus,
.form-control[type="date"]:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Colori per stati progetto */
.bg-success {
  background-color: #198754 !important;
}

.bg-secondary {
  background-color: #6c757d !important;
}

.bg-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

/* Stili responsive per il modal */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem;
    max-width: none;
  }
  
  .modal-body .row .col-md-4,
  .modal-body .row .col-md-8 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 0.5rem;
  }
}
</style>